<!--
  AI 客服最小统计（REQ-2026-574277 §10.4）。只读，不建新表，数字全部由审核队列现有字段聚合。

  两种摆法：
    · compact（工作台左栏，很窄）：一行「今日」摘要，鼠标移上去看今日 / 近 7 天的完整对照
    · 默认（AI 回复记录页顶部）：整行铺开

  ⚠️ 「超时未回复」是这行数字里最重要的一个：= 会话静默到被自动归档都没有人回（reviewer_name='系统(归档)'）。
     它和人工忽略、系统(合并)、批量忽略是四种不同的"已忽略"，别合并显示成一个"忽略数" ——
     合并之后这个数会被另外三种稀释，看不出"客户被晾着"这件事到底有多少。

  ⚠️ 平均响应分「AI 直答」和「人工」两个数，不揉成一个：AI 是秒级、人工是分钟到小时级，
     揉在一起的话 AI 直答多的那天数字会好看得离谱，而人工那头可能正在恶化。
-->
<template>
  <div v-if="loaded" class="ai-cs-stats" :class="{ compact }">
    <template v-if="compact">
      <el-popover placement="right-start" trigger="hover" width="420">
        <table class="ai-cs-stats-table">
          <thead>
            <tr><th></th><th v-for="c in columns" :key="c.key">{{ c.label }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="r in ranges" :key="r.key">
              <td class="range">{{ r.label }}</td>
              <td v-for="c in columns" :key="c.key" :class="{ warn: c.warn && cell(r.key, c) !== '0' && cell(r.key, c) !== '-' }">
                {{ cell(r.key, c) }}
              </td>
            </tr>
          </tbody>
        </table>
        <div slot="reference" class="ai-cs-stats-line">
          今日 直答 <b>{{ num('today', 'aiDirect') }}</b>
          · 审后发 <b>{{ num('today', 'reviewedSent') }}</b>
          · 超时未回 <b :class="{ warn: num('today', 'timeoutNoReply') > 0 }">{{ num('today', 'timeoutNoReply') }}</b>
        </div>
      </el-popover>
    </template>
    <template v-else>
      <div v-for="r in ranges" :key="r.key" class="ai-cs-stats-row">
        <span class="range">{{ r.label }}</span>
        <span v-for="c in columns" :key="c.key" class="item">
          {{ c.label }}
          <b :class="{ warn: c.warn && cell(r.key, c) !== '0' && cell(r.key, c) !== '-' }">{{ cell(r.key, c) }}</b>
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import aiReviewApi from '@/api/im/aiReview'

export default {
  name: 'AiCsStats',
  props: {
    compact: { type: Boolean, default: false }
  },
  data() {
    return {
      loaded: false,
      stats: { today: {}, last7Days: {} },
      ranges: [
        { key: 'today', label: '今日' },
        { key: 'last7Days', label: '近7天' }
      ],
      columns: [
        { key: 'aiDirect', label: 'AI直答' },
        { key: 'pending', label: '待审' },
        { key: 'reviewedSent', label: '审核后发出' },
        { key: 'timeoutNoReply', label: '超时未回复', warn: true },
        { key: 'avgAiLatencyMs', label: 'AI平均', format: 'ms' },
        { key: 'avgHumanSeconds', label: '人工平均', format: 'sec' }
      ]
    }
  },
  created() {
    this.load()
    // 草稿被处理之后数字会变；工作台里已经有这个事件了，跟着刷就行，不另起定时器
    this.$EventBus.$on('aiReviewChanged', this.load)
  },
  beforeDestroy() {
    this.$EventBus.$off('aiReviewChanged', this.load)
  },
  methods: {
    load() {
      aiReviewApi.stats().then(({ data }) => {
        this.stats = {
          today: (data && data.today) || {},
          last7Days: (data && data.last7Days) || {}
        }
        this.loaded = true
      }).catch(() => {
        // 统计挂了不能连累工作台：整块不显示就是了（loaded 保持 false）
      })
    },
    num(range, key) {
      return Number((this.stats[range] || {})[key]) || 0
    },
    cell(range, col) {
      const v = (this.stats[range] || {})[col.key]
      if (col.format === 'ms') return v == null ? '-' : this.duration(Number(v) / 1000)
      if (col.format === 'sec') return v == null ? '-' : this.duration(Number(v))
      return String(Number(v) || 0)
    },
    /** 秒 → 人读得懂的时长。平均值没有样本时后端给 null，上面已经显示成 "-" 了 */
    duration(sec) {
      if (sec < 60) return (sec < 10 ? sec.toFixed(1) : Math.round(sec)) + '秒'
      if (sec < 3600) return Math.round(sec / 60) + '分钟'
      return (sec / 3600).toFixed(1) + '小时'
    }
  }
}
</script>

<style scoped>
.ai-cs-stats { font-size: 12px; color: #606266; }
.ai-cs-stats b { color: #303133; margin: 0 2px; }
.ai-cs-stats b.warn, .ai-cs-stats-table td.warn { color: #f56c6c; font-weight: 600; }
.ai-cs-stats.compact { padding: 6px 15px; border-bottom: 1px solid #f0f0f0; }
.ai-cs-stats-line { cursor: default; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ai-cs-stats-row { padding: 4px 0; }
.ai-cs-stats-row .range { display: inline-block; width: 48px; color: #909399; }
.ai-cs-stats-row .item { margin-right: 22px; }
.ai-cs-stats-table { width: 100%; border-collapse: collapse; font-size: 12px; text-align: center; }
.ai-cs-stats-table th { color: #909399; font-weight: normal; padding: 4px 2px; }
.ai-cs-stats-table td { padding: 4px 2px; }
.ai-cs-stats-table td.range { color: #909399; text-align: left; }
</style>
